import type { Block } from '@auditwire/contracts';
import { CategoryPill } from '@/entities/article';
import { Avatar } from '@/shared/ui';
import { SITE } from '@/shared/config/site';
import { formatDate } from '@/shared/lib/utils/date';
import { HIGH_FETCH_PRIORITY } from '@/shared/lib/utils/imgPriority';
import { ArticleBody } from './ArticleBody';

export interface ArticleReadingViewProps {
  title: string;
  excerpt: string;
  category: string;
  coverUrl: string;
  body: Block[];
  readTime: number;
  publishedAt: string | null;
  author: {
    name: string;
    initials: string;
    avatarColor: string;
    avatarUrl?: string | null;
  } | null;
  /** Editor preview: show muted stand-ins instead of collapsing empty fields. */
  showPlaceholders?: boolean;
}

/**
 * The canonical article reading layout — headline, byline, cover and body.
 *
 * Shared by the public article page and the editor's preview so "preview" means
 * literally the same component, not a lookalike that can drift out of sync.
 */
export function ArticleReadingView({
  title,
  excerpt,
  category,
  coverUrl,
  body,
  readTime,
  publishedAt,
  author,
  showPlaceholders = false,
}: ArticleReadingViewProps) {
  return (
    <>
      <CategoryPill category={category} className="mb-4 block" />

      <h1 className="mb-6 font-display text-5xl font-black italic leading-[1.03] tracking-tight text-foreground sm:text-[3.5rem]">
        {title || (showPlaceholders ? <span className="text-muted-foreground">Untitled</span> : null)}
      </h1>

      {excerpt ? (
        <p className="mb-8 text-xl leading-relaxed text-muted-foreground">{excerpt}</p>
      ) : showPlaceholders ? (
        <p className="mb-8 text-xl italic leading-relaxed text-muted-foreground/60">
          Add a subtitle to summarise the piece.
        </p>
      ) : null}

      <div className="mb-10 flex items-center gap-3 border-y border-border py-4">
        {author ? (
          <Avatar
            initials={author.initials}
            color={author.avatarColor}
            src={author.avatarUrl}
            alt={`${author.name}'s profile picture`}
            size="md"
          />
        ) : null}
        <div>
          <p className="text-sm font-semibold text-foreground">{author?.name ?? SITE.name}</p>
          <p className="font-mono text-[10px] text-muted-foreground">
            {/* Machine-readable date for crawlers; the visible text stays human. */}
            {publishedAt ? (
              <time dateTime={publishedAt}>{formatDate(publishedAt)}</time>
            ) : (
              formatDate(publishedAt)
            )}{' '}
            · {readTime} min read
          </p>
        </div>
      </div>

      {coverUrl ? (
        <div className="mb-12 aspect-[16/9] overflow-hidden rounded-3xl bg-zinc-100 dark:bg-zinc-800">
          {/*
            The cover is almost always the LCP element: load it eagerly at high
            priority rather than letting it queue behind the body. Alt text
            carries the headline so the image is findable in image search — the
            aspect-ratio box above already reserves its space, so no CLS.
          */}
          <img
            src={coverUrl}
            alt={title ? `Cover image for “${title}”` : ''}
            className="h-full w-full object-cover"
            loading="eager"
            decoding="async"
            {...HIGH_FETCH_PRIORITY}
          />
        </div>
      ) : null}

      {body.length > 0 ? (
        <ArticleBody blocks={body} />
      ) : showPlaceholders ? (
        <p className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Your article body will appear here.
        </p>
      ) : null}
    </>
  );
}
